<template>
  <div class="toast-container">
    <div
      v-for="toast in toasts"
      :key="toast.id"
      :class="['toast', toast.type, { active: toast.active }]"
    >
      <div class="toast-wrapper">
        <img
          width="26"
          height="26"
          loading="lazy"
          src="~dashboard/assets/images/new-chat.png"
          alt="new conversation"
        />
        <div class="toast-content">
          <div v-if="toast.data" class="message">
            <span class="text text-1">{{ toast.message }}</span>
            <span class="text text-2">{{ toast.data.sender.name || '' }}</span>
            <div class="channel-tag">
              <fluent-icon
                class="icon"
                :icon="getInboxIcon(toast.data.channel)"
                size="16"
              />
              <span
                class="text text-2 channel-name"
                :title="toast.data.inbox_name"
              >
                {{ toast.data.inbox_name || '' }}
              </span>
            </div>
          </div>
          <div class="buttons-toast">
            <woot-button
              v-if="toast.hasAllowRej"
              style="border-radius: 12px"
              variant="smooth"
              @click="handleReject(toast)"
            >
              Reject
            </woot-button>
            <woot-button
              style="border-radius: 12px"
              @click="handleAllow(toast)"
            >
              Answer
            </woot-button>
          </div>
        </div>
      </div>

      <div
        class="progress"
        :class="{ active: toast.active }"
        :style="{ '--progress-duration': toast.duration + 'ms' }"
      />
    </div>
  </div>
</template>

<script>
import { getInboxClassByType } from 'dashboard/helper/inbox';

export default {
  name: 'ChatToastify',
  data() {
    return {
      toasts: [],
      data: null,
    };
  },
  mounted() {
    // eslint-disable-next-line no-unused-expressions
    this.$toastBus?.$on(
      'show-toast',
      ({
        message,
        type,
        duration,
        hasAllowRej,
        onAllow,
        onReject,
        data,
        handleId,
      }) => {
        this.data = data;
        this.showToast(
          message,
          type,
          duration,
          hasAllowRej,
          onAllow,
          onReject,
          data,
          handleId
        );
      }
    );
    // eslint-disable-next-line no-unused-expressions
    this.$toastBus?.$on('close-toast', ({ id }) => {
      this.closeToastById(id);
    });
  },
  methods: {
    getInboxIcon(type) {
      return getInboxClassByType(type);
    },
    showToast(
      message,
      type = 'default',
      duration = 5000,
      hasAllowRej = false,
      onAllow = null,
      onReject = null,
      data = null,
      handleId = null
    ) {
      const id = new Date().getTime() + Math.random();
      const toast = {
        id,
        message,
        type,
        duration,
        hasAllowRej,
        onAllow,
        onReject,
        data,
        handleId,
        active: false,
        timer1: null,
        timer2: null,
      };
      this.toasts.push(toast);
      toast.handleId(id);
      const activateTimer = setTimeout(() => {
        this.setActive(id, true);
      }, 1);

      toast.timer1 = setTimeout(() => {
        this.setActive(id, false);
      }, duration);
      toast.timer2 = setTimeout(() => {
        this.removeToast(id);
      }, duration + 400);
      toast.activateTimer = activateTimer;
    },
    closeToastById({ id }) {
      this.removeToast(id);
    },

    setActive(id, value) {
      const index = this.toasts.findIndex(t => t.id === id);
      if (index !== -1) {
        this.$set(this.toasts, index, { ...this.toasts[index], active: value });
      }
    },

    removeToast(id) {
      const index = this.toasts.findIndex(t => t.id === id);
      if (index !== -1) {
        this.toasts.splice(index, 1);
      }
    },
    handleAllow(toast) {
      if (typeof toast.onAllow === 'function') {
        toast.onAllow(toast.id);
      }
      this.removeToast(toast.id);
    },
    handleReject(toast) {
      if (typeof toast.onReject === 'function') {
        toast.onReject();
      }
      this.removeToast(toast.id);
    },

    closeToast(id) {
      const toast = this.toasts.find(t => t.id === id);
      if (toast) {
        // Clear any timers so they don't interfere
        clearTimeout(toast.activateTimer);
        clearTimeout(toast.timer1);
        clearTimeout(toast.timer2);
        // Remove the "active" class to begin the exit animation
        this.setActive(id, false);
        // Remove the toast after the exit animation (400ms)
        setTimeout(() => {
          this.removeToast(id);
        }, 400);
      }
    },

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/variables.scss';

:root {
  --progress-duration: 5000ms;
}

.toast-container {
  position: fixed;
  // TODO: changed bottom for widget icon temporary
  bottom: 90px;
  right: 30px;
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
  z-index: 1000;
}

.toast {
  position: relative;
  width: 400px;
  min-height: 130px;
  border-radius: 20px;
  background: var(--v-50);
  padding: 16px;
  box-shadow: 0 6px 20px -5px rgba(0, 0, 0, 0.1);
  display: flex;
  overflow-x: hidden;
  align-items: center;
  transform: translateX(calc(100% + 30px));
  transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.35);
}
.toast-wrapper {
  width: 100%;
  display: flex;
  align-items: start;
  gap: 16px;
}
.toast.active {
  transform: translateX(0%);
}

.toast-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  .message {
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;
  }

  .buttons-toast {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    margin-left: 15px;
  }
}

.message {
  .text {
    font-size: var(--font-size-paragraph-two);
    font-weight: 500;
    color: #666666;
  }
  .channel-tag {
    display: inline-flex;
    align-items: center;
    background-color: var(--s-50);
    color: #333;
    padding: 0.25rem 0.5rem;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    margin-top: 10px;
    margin-bottom: 0.5rem;
    max-width: 150px;
    overflow: hidden;
    .icon {
      margin-right: 0.4rem;
      flex-shrink: 0;
    }
    .channel-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: inline-block;
    }
  }
}

.message .text.text-1 {
  font-size: var(--font-size-body-tree);
  padding-bottom: 18px;
  font-weight: 700;
  color: var(--s-700);
}

.toast .close {
  position: absolute;
  top: 10px;
  right: 15px;
  padding: 5px;
  cursor: pointer;
  opacity: 0.7;
}

.toast .close:hover {
  opacity: 1;
}

.toast .progress {
  position: absolute;
  bottom: -8px;
  left: 0;
  height: 4px;
  width: 100%;
  background-color: var(--t-200);
}

.toast .progress:before {
  content: '';
  position: absolute;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100%;
  background-color: var(--t-200);
}

.progress.active:before {
  animation: progress var(--progress-duration) linear forwards;
}

@keyframes progress {
  100% {
    right: 100%;
  }
}
.toast.default .progress:before {
  background: $color-woot;
}
</style>
